import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import Field from "components/Field";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import { useIntl } from "react-intl";
import { withTypes } from "react-final-form";
import {
  CarryoverRuleRequest,
  CycleConfigurationRequest,
  IRequirementAuthoringOptions,
  IRequirementSetDetail,
  RequirementSetRequest,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import DateOnlyPicker from "./DateOnlyPicker";
import { toDateInputValue, toDateOnly } from "./dateOnlyField";
import ensureOk from "./ensureOk";
import { DateSchemeTypeValue, selectedEnumValue } from "./lookupEnums";
import NotesSection from "./NotesSection";
import styles from "./styles";

type SetFormValues = {
  name?: string;
  description?: string;
  effectiveDate?: string;
  endDate?: string;
  cycleLengthMonths?: number | string;
  cycleTypeId?: string;
  dateSchemeTypeId?: string;
  fixedMonth?: number | string;
  fixedDay?: number | string;
  oddEvenYear?: number | string;
  earnPeriodsPerReportPeriod?: number | string;
  gracePeriodDays?: number | string;
  carryoverAllowed?: boolean;
  maxHours?: number | string;
  earnWindowMonths?: number | string;
  maxPeriodsForward?: number | string;
  appliesToTopicRequirements?: boolean;
};

const { Form } = withTypes<SetFormValues>();

type RequirementSetPaneProps = {
  set: IRequirementSetDetail;
  options?: IRequirementAuthoringOptions;
  locked: boolean;
  onSaved: () => void;
  onNotesChanged: () => void;
  /** The page owns the snackbar, so a refused note deletion is reported through it. */
  onError: (message: string) => void;
};

const asNumber = (value?: number | string) =>
  value === "" || value === undefined || value === null ? undefined : Number(value);

const RequirementSetPane = ({
  set,
  options,
  locked,
  onSaved,
  onNotesChanged,
  onError,
}: RequirementSetPaneProps) => {
  const { formatMessage: t } = useIntl();
  const { requirementSetService } = useGlobalContext();

  const cycle = set.cycleConfiguration;
  const carryover = set.carryoverRule;

  const initialValues: SetFormValues = {
    name: set.name ?? "",
    description: set.description ?? "",
    effectiveDate: toDateInputValue(set.effectiveDate),
    endDate: toDateInputValue(set.endDate),
    cycleLengthMonths: cycle?.cycleLengthMonths ?? 24,
    // Defaulted to the first option rather than blank: an empty select would make the whole cycle
    // configuration unsaveable, and silently so.
    cycleTypeId: cycle?.cycleTypeId ?? options?.cycleTypes?.[0]?.id ?? "",
    dateSchemeTypeId:
      cycle?.dateSchemeTypeId ?? options?.dateSchemeTypes?.[0]?.id ?? "",
    fixedMonth: cycle?.fixedMonth ?? "",
    fixedDay: cycle?.fixedDay ?? "",
    oddEvenYear: cycle?.oddEvenYear ?? "",
    earnPeriodsPerReportPeriod: cycle?.earnPeriodsPerReportPeriod ?? 1,
    gracePeriodDays: cycle?.gracePeriodDays ?? 0,
    carryoverAllowed: !!set.carryoverAllowed,
    maxHours: carryover?.maxHours ?? "",
    earnWindowMonths: carryover?.earnWindowMonths ?? "",
    maxPeriodsForward: carryover?.maxPeriodsForward ?? "",
    appliesToTopicRequirements: !!carryover?.appliesToTopicRequirements,
  };

  const handleSubmit = async (values: SetFormValues) => {
    // Checked before anything is written: the three saves below are separate calls, so a refusal
    // half-way would leave the set renamed and its cycle untouched.
    // A blank cycle length used to be sent as 0, and nothing downstream refuses a zero-month cycle.
    if (
      !values.cycleTypeId ||
      !values.dateSchemeTypeId ||
      !((asNumber(values.cycleLengthMonths) ?? 0) > 0)
    ) {
      return { [FORM_ERROR]: t({ id: "cycleConfigurationIncomplete" }) };
    }

    if (
      values.endDate &&
      values.effectiveDate &&
      values.endDate < values.effectiveDate
    ) {
      return { [FORM_ERROR]: t({ id: "endDateBeforeEffectiveDate" }) };
    }

    try {
      const savedSet = await requirementSetService.saveRequirementSet(
        RequirementSetRequest.fromJS({
          id: set.id,
          licenseId: set.licenseId,
          name: values.name,
          // The save replaces the whole set: an empty description clears it.
          description: values.description?.trim() || undefined,
          effectiveDate: toDateOnly(values.effectiveDate),
          endDate: toDateOnly(values.endDate),
        })
      );

      ensureOk(savedSet);

      {
        const savedCycle = await requirementSetService.saveCycleConfiguration(
          CycleConfigurationRequest.fromJS({
            requirementSetId: set.id,
            cycleLengthMonths: asNumber(values.cycleLengthMonths) ?? 0,
            cycleTypeId: values.cycleTypeId,
            dateSchemeTypeId: values.dateSchemeTypeId,
            fixedMonth: asNumber(values.fixedMonth),
            fixedDay: asNumber(values.fixedDay),
            oddEvenYear: asNumber(values.oddEvenYear),
            earnPeriodsPerReportPeriod:
              asNumber(values.earnPeriodsPerReportPeriod) ?? 1,
            gracePeriodDays: asNumber(values.gracePeriodDays) ?? 0,
          })
        );

        ensureOk(savedCycle);
      }

      // Carryover has no "allowed" column: switching it off removes the row.
      if (values.carryoverAllowed) {
        const savedCarryover = await requirementSetService.saveCarryoverRule(
          CarryoverRuleRequest.fromJS({
            requirementSetId: set.id,
            maxHours: asNumber(values.maxHours),
            earnWindowMonths: asNumber(values.earnWindowMonths),
            maxPeriodsForward: asNumber(values.maxPeriodsForward),
            appliesToTopicRequirements: !!values.appliesToTopicRequirements,
          })
        );

        ensureOk(savedCarryover);
      } else if (set.carryoverAllowed) {
        ensureOk(await requirementSetService.deleteCarryoverRule(set.id!));
      }

      onSaved();
    } catch (error) {
      // Reloaded even though the save failed: the three calls above are not one transaction, so an
      // earlier one may have gone through, and the form must not keep showing state the database
      // does not have.
      onSaved();

      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  const numberField = (
    name: keyof SetFormValues,
    labelId: string,
    step?: string
  ) => (
    <Field name={name}>
      {({ input }) => (
        <Grid item xs={12} sm={4}>
          <TextField
            variant="standard"
            fullWidth
            disabled={locked}
            label={t({ id: labelId })}
            InputProps={{ type: "number", inputProps: { step } }}
            {...input}
          />
        </Grid>
      )}
    </Field>
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={styles.paneHead}>
        <Typography variant="h6">{t({ id: "requirementSet" })}</Typography>
      </Box>

      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, submitting, submitError, values, form }) => (
          <form onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2}>
              <Field name="name" required>
                {({ input, meta }) => (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      required
                      variant="standard"
                      fullWidth
                      disabled={locked}
                      label={t({ id: "setName" })}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="effectiveDate" required>
                {({ input, meta }) => (
                  <Grid item xs={12} sm={4}>
                    <DateOnlyPicker
                      required
                      disabled={locked}
                      label={t({ id: "effectiveDate" })}
                      value={input.value}
                      onChange={input.onChange}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="endDate">
                {({ input }) => (
                  <Grid item xs={12} sm={4}>
                    <DateOnlyPicker
                      disabled={locked}
                      label={t({ id: "endDateLabel" })}
                      value={input.value}
                      onChange={input.onChange}
                      helperText={t({ id: "leaveBlankForOpenEndedSet" })}
                    />
                  </Grid>
                )}
              </Field>

              <Grid item xs={12} sm={4}>
                <TextField
                  variant="standard"
                  fullWidth
                  disabled
                  label={t({ id: "license" })}
                  value={set.licenseDisplayName ?? ""}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  variant="standard"
                  fullWidth
                  disabled
                  label={t({ id: "approvedBy" })}
                  value={set.approvedBy ?? "—"}
                />
              </Grid>

              <Field name="description">
                {({ input }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      multiline
                      minRows={2}
                      disabled={locked}
                      label={t({ id: "descriptionLabel" })}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>
            </Grid>

            <Accordion sx={{ mt: 3 }} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{t({ id: "cycleConfiguration" })}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {numberField("cycleLengthMonths", "cycleLengthMonths")}

                  <Field name="cycleTypeId">
                    {({ input }) => (
                      <Grid item xs={12} sm={4}>
                        <TextField
                          select
                          variant="standard"
                          fullWidth
                          disabled={locked}
                          label={t({ id: "cycleType" })}
                          {...input}
                        >
                          {(options?.cycleTypes ?? []).map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                  </Field>

                  <Field name="dateSchemeTypeId">
                    {({ input }) => (
                      <Grid item xs={12} sm={4}>
                        <TextField
                          select
                          variant="standard"
                          fullWidth
                          disabled={locked}
                          label={t({ id: "dateScheme" })}
                          {...input}
                        >
                          {(options?.dateSchemeTypes ?? []).map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                  </Field>

                  {/* Only a fixed-date scheme has a month, a day and an odd/even year. */}
                  {selectedEnumValue(
                    options?.dateSchemeTypes,
                    values.dateSchemeTypeId
                  ) === DateSchemeTypeValue.FixedDate && (
                    <>
                      {numberField("fixedMonth", "fixedMonth")}
                      {numberField("fixedDay", "fixedDay")}
                      {numberField("oddEvenYear", "oddEvenYear")}
                    </>
                  )}

                  {numberField(
                    "earnPeriodsPerReportPeriod",
                    "earnPeriodsPerReportPeriod"
                  )}
                  {numberField("gracePeriodDays", "gracePeriodDays")}
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion sx={{ mt: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{t({ id: "creditConfiguration" })}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* One credit configuration per licence, shared by every one of its sets, so editing
                    it from inside a draft would quietly change the rules of the governing set. */}
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t({ id: "creditConfigurationSharedNotice" })}
                </Alert>

                {set.creditConfiguration ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        variant="standard"
                        fullWidth
                        disabled
                        label={t({ id: "creditUnit" })}
                        value={set.creditConfiguration.creditUnitMinutes ?? ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        variant="standard"
                        fullWidth
                        disabled
                        label={t({ id: "minimumIncrement" })}
                        value={set.creditConfiguration.minCreditIncrement ?? ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        variant="standard"
                        fullWidth
                        disabled
                        label={t({ id: "roundingDirection" })}
                        value={
                          set.creditConfiguration.roundingDirectionTypeName ?? ""
                        }
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Alert severity="info">
                    {t({ id: "noCreditConfiguration" })}
                  </Alert>
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion sx={{ mt: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{t({ id: "carryoverRules" })}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Field name="carryoverAllowed" type="checkbox">
                  {({ input }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={input.checked}
                          disabled={locked}
                          onChange={(event) => {
                            input.onChange(event);
                            if (!event.target.checked) {
                              form.change("maxHours", "");
                              form.change("earnWindowMonths", "");
                              form.change("maxPeriodsForward", "");
                              form.change("appliesToTopicRequirements", false);
                            }
                          }}
                        />
                      }
                      label={t({ id: "carryoverAllowedForSet" })}
                    />
                  )}
                </Field>

                {/* The fields exist only while carryover is on, matching the row-presence model. */}
                {values.carryoverAllowed && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {numberField("maxHours", "maxHours", "0.25")}
                    {numberField("earnWindowMonths", "earnWindowMonths")}
                    {numberField("maxPeriodsForward", "maxPeriodsForward")}

                    <Field name="appliesToTopicRequirements" type="checkbox">
                      {({ input }) => (
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={input.checked}
                                disabled={locked}
                                onChange={input.onChange}
                              />
                            }
                            label={t({ id: "appliesToSubjectAreaRequirements" })}
                          />
                        </Grid>
                      )}
                    </Field>
                  </Grid>
                )}
              </AccordionDetails>
            </Accordion>

            {submitError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {submitError}
              </Alert>
            )}

            <Box sx={styles.actionBar}>
              <Button
                type="submit"
                variant="contained"
                disabled={locked || submitting}
                startIcon={submitting && <CircularProgress size={16} />}
              >
                {t({ id: "updateRequirementSet" })}
              </Button>
            </Box>
          </form>
        )}
      />

      {/* Outside the form: each note is saved on its own, not with the set. */}
      <NotesSection
        requirementSetId={set.id!}
        notes={set.notes ?? []}
        options={options}
        locked={locked}
        onChanged={onNotesChanged}
        onError={onError}
      />
    </Paper>
  );
};

export default RequirementSetPane;
